import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  TablePagination
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useApiQuery } from '../../hooks/useApiCache';

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 3,
  backgroundColor: theme.palette.background.paper,
  borderRight: `2px solid ${theme.palette.divider}`,
  fontWeight: 600
}));

const EquipesTempoTurnoModal = ({ open, onClose, startDate, endDate, contrato, centro, equipe }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch data with React Query cache
  const { data: rawData, isLoading: loading, error: queryError } = useApiQuery(
    '/turnos-ofensores-dia',
    { startDate, endDate, contrato, centro, equipe },
    {
      enabled: open && !!startDate && !!endDate,
      staleTime: 5 * 60 * 1000
    }
  );

  const error = queryError?.message || null;

  // Process raw data into equipesData and dias
  const { equipesData, dias } = useMemo(() => {
    const data = Array.isArray(rawData) ? rawData : [];
    if (data.length === 0) return { equipesData: [], dias: [] };

    const diasSet = new Set();
    const equipesMap = {};

    data.forEach((diaItem) => {
      const dataStr = diaItem.data;
      if (!dataStr) return;
      diasSet.add(dataStr);
      if (Array.isArray(diaItem.equipes)) {
        diaItem.equipes.forEach((eq) => {
          const nome = eq.des_equipe || 'Não informado';
          if (!equipesMap[nome]) {
            equipesMap[nome] = { nome, dias: {}, mediaPerc: 0, somaPerc: 0, countPerc: 0, mediaTempo: 0, somaTempo: 0, countTempo: 0 };
          }
          const tempoValor =
            (typeof eq.tempo_medio_turno === 'number' && eq.tempo_medio_turno) ||
            (typeof eq.tempo_medio_saida_base === 'number' && eq.tempo_medio_saida_base) ||
            (typeof eq.tempo_medio_saida_base_minutos === 'number' && eq.tempo_medio_saida_base_minutos) ||
            (typeof eq.tempo_medio_turno === 'string' && Number(eq.tempo_medio_turno)) ||
            (typeof eq.tempo_medio_saida_base === 'string' && Number(eq.tempo_medio_saida_base)) ||
            (typeof eq.tempo_medio_saida_base_minutos === 'string' && Number(eq.tempo_medio_saida_base_minutos)) ||
            null;
          const percValor =
            (typeof eq.percentual_aderencia === 'number' && eq.percentual_aderencia) ||
            (typeof eq.percentual_turno === 'number' && eq.percentual_turno) ||
            (typeof eq.percentual_aderencia === 'string' && Number(eq.percentual_aderencia)) ||
            (typeof eq.percentual_turno === 'string' && Number(eq.percentual_turno)) ||
            null;
          equipesMap[nome].dias[dataStr] = {
            tempo: Number.isFinite(tempoValor) ? tempoValor : null,
            perc: Number.isFinite(percValor) ? percValor : null
          };
          if (Number.isFinite(percValor)) {
            equipesMap[nome].somaPerc += Number(percValor);
            equipesMap[nome].countPerc += 1;
          }
          if (Number.isFinite(tempoValor)) {
            equipesMap[nome].somaTempo += Number(tempoValor);
            equipesMap[nome].countTempo += 1;
          }
        });
      }
    });

    const diasArr = Array.from(diasSet).sort();
    const equipesArr = Object.values(equipesMap).map((e) => ({
      ...e,
      mediaPerc: e.countPerc > 0 ? e.somaPerc / e.countPerc : 0,
      mediaTempo: e.countTempo > 0 ? e.somaTempo / e.countTempo : 0
    }));
    equipesArr.sort((a, b) => Math.abs(a.mediaPerc) - Math.abs(b.mediaPerc));

    return { equipesData: equipesArr, dias: diasArr };
  }, [rawData]);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ m: 0, p: 3, pb: 2 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 700 }}>
          Histórico — Tempo Médio do Turno e % Aderência
        </Typography>
        <Typography variant="body2" component="div" color="text.secondary">
          Ranking por média de % de aderência no período
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : equipesData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: '60vh', borderRadius: 2, boxShadow: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StickyTableCell sx={{ minWidth: 80 }}>Ranking</StickyTableCell>
                  <StickyTableCell sx={{ minWidth: 250 }}>Equipe</StickyTableCell>
                  <StickyTableCell align="center" sx={{ minWidth: 140 }}>Média % Aderência</StickyTableCell>
                  <StickyTableCell align="center" sx={{ minWidth: 160 }}>Média do Turno</StickyTableCell>
                  {dias.map((d) => (
                    <TableCell key={d} align="center" sx={{ minWidth: 100, borderLeft: '1px solid #e0e0e0', fontWeight: 600, fontSize: '0.75rem' }}>
                      {formatDateDisplay(d)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {equipesData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => {
                  const globalIndex = page * rowsPerPage + idx;
                  let rankBg = 'success.main';
                  if (globalIndex >= 3 && globalIndex <= 5) rankBg = 'warning.main';
                  if (globalIndex >= 6) rankBg = 'error.main';
                  return (
                    <TableRow key={row.nome} hover>
                      <StickyTableCell>
                        <Chip label={`#${globalIndex + 1}`} size="small" sx={{ bgcolor: rankBg, color: 'white', fontWeight: 700, minWidth: 50 }} />
                      </StickyTableCell>
                      <StickyTableCell sx={{ fontWeight: 600 }}>{row.nome}</StickyTableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>{Number.isFinite(row.mediaPerc) ? `${Number(row.mediaPerc).toFixed(2)}%` : '-'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: 'text.primary' }}>{Number.isFinite(row.mediaTempo) ? `${Number(row.mediaTempo).toFixed(2)} min` : '-'}</TableCell>
                      {dias.map((d) => {
                        const cell = row.dias[d];
                        const tempo = cell?.tempo;
                        const perc = cell?.perc;
                        const hasValues = Number.isFinite(tempo) || Number.isFinite(perc);
                        const tempoText = Number.isFinite(tempo) ? `${Number(tempo).toFixed(2)} min` : '-';
                        const percText = Number.isFinite(perc) ? `${Number(perc).toFixed(2)}%` : '-';
                        return (
                          <TableCell key={d} align="center" sx={{ borderLeft: '1px solid #e0e0e0' }}>
                            {!hasValues ? (
                              <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: '#e0e0e0', m: '0 auto' }} />
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{tempoText}</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{percText}</Typography>
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={equipesData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Equipes por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button onClick={onClose} variant="outlined" color="primary" sx={{ fontWeight: 600 }}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipesTempoTurnoModal;
